import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import axios from 'axios';
import produce from 'immer';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from '../../commons/constants/constants';
import { uploadFiles } from '../../services/uploader.service';
import { FileModel, PageRequest, PaginationType } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/draftTour.entity';
import { RootModel } from '../models';
import { storeName as userStore } from './user.model';

export const storeName = 'draftTour';

export interface initialValuesType {
  ids: (number | string)[];
  pagination: PaginationType | null;
  criteria: { [key: string]: any };
  loading: boolean;
  visible: boolean;
  floorplanVisible: boolean;
  scannerVisible: boolean;
  uploadVisible: boolean;
  editId: number;
}

const initialState: initialValuesType = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  loading: false,
  visible: false,
  floorplanVisible: false,
  scannerVisible: false,
  uploadVisible: false,
  editId: null,
};

export const draftTour: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setFloorplanVisible: produce((state, payload) => {
      state.floorplanVisible = payload;
    }),
    setScannerVisible: produce((state, payload) => {
      state.scannerVisible = payload;
    }),
    setUploadVisible: produce((state, payload) => {
      state.uploadVisible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof draftTour>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].loadDraftTours(params);
        if (data && data.content) {
          this.setIds(data.content.map((tour) => tour.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filter tours');
      } finally {
        this.setLoading(false);
      }
    },
    getDraftTour(payload) {
      try {
        return dispatch[entityStore].loadDraftTourById(payload);
      } catch (e) {
        console.error(e);
        message.error(`Fail to get draft tour ${payload}`);
      }
    },
    async getEditDraftTour(payload) {
      this.setLoading(true);
      try {
        const { id, mode = undefined } = payload;
        this.getDraftTour(id);
        this.setEditId(id);
        switch (mode) {
          case 'designer':
            this.setFloorplanVisible(true);
            break;
          case 'scanner':
            this.setScannerVisible(true);
            break;
          case 'upload':
            this.setUploadVisible(true);
            break;
          default:
            this.setVisible(true);
            break;
        }
      } catch (e) {
        console.error(e);
        message.error('Fail to get draft tour');
      } finally {
        this.setLoading(false);
      }
    },
    async saveAsync(payload, state) {
      this.setLoading(true);
      let item;
      try {
        await dispatch[entityStore].saveDraft(payload);

        if (!payload.id) {
          this.resetFilter();
        } else {
          this.reloadList();
        }

        this.setVisible(false);
        message.success('Success to save draft tour');
      } catch (e) {
        console.error(e);
        message.error('Fail to save draft tour');
      } finally {
        this.setLoading(false);
      }
      return item;
    },
    async delete(id) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].deleteDraft(id);
        await this.reloadList();
        message.success('Success to delete draft tour');
      } catch (e) {
        console.error(e);
        message.error('Fail to delete draft tour');
      } finally {
        this.setLoading(false);
      }
    },
    async makeScenesForMarkers(payload = [], rootState) {
      this.setLoading(true);
      try {
        const { editId } = rootState[storeName];
        const { user } = rootState[userStore];

        const uploadActions = [];
        const request = [...payload];
        const imageFiles = request.map((x) => (x.file as UploadFile).originFileObj);
        if (imageFiles && !!imageFiles.length) {
          uploadActions.push(
            uploadFiles(editId, imageFiles, FILE_NAME_TYPE.PANO, user.id, 'scenes', true)
          );
        }
        const files = await axios.all(uploadActions);
        const fileModels: FileModel[] = files.flatMap((file) => file.filesList || []);
        fileModels.forEach((f) => {
          const { url, id, originalName } = f;
          if (!originalName) {
            return;
          }

          const marker = request.find((rq) => rq.file.name === originalName);
          if (marker) {
            marker.file = {
              url,
              id,
            };
          }
        });

        await dispatch[entityStore].makeScenesForMarkers({
          markerScenes: payload,
          tourId: editId,
        });
        await this.reloadList();
        message.success('Success to update draft tour');
      } catch (e) {
        console.error(e);
        message.error('Fail to update draft tour');
      } finally {
        this.setLoading(false);
      }
    },
    async makeKrpanoTour(payload) {
      this.setLoading(true);
      try {
        const result = await dispatch[entityStore].makeTour(payload);
        if (result) {
          await this.reloadList();
          message.success('Success to make draft tour');
        } else {
          message.error('Fail to make draft tour');
        }
      } catch (e) {
        console.error(e);
        message.error('Fail to make draft tour');
      } finally {
        this.setLoading(false);
      }
    },
    async updateDraftTourState(payload, rootState) {
      this.setLoading(true);
      try {
        const { editId } = rootState[storeName];
        await dispatch[entityStore].updateState({ id: editId, state: payload });
        message.success('Success to update draft tour');
        this.reloadList();
        this.setFloorplanVisible(false);
        this.setEditId(null);
      } catch (e) {
        console.error(e);
        message.error('Fail to update draft tour');
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default draftTour;
